import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { CompactError, RobbieOverlay, StepBar } from '../../../components/ui';
import type { EndUserHandoffView } from '../../../hooks/useEndUserHandoff';
import { plural, validationCopy } from '../../../utils/waitCopy';
import { ValidationStepState } from './useValidationStep';

/**
 * Step 3b's wait and forward bar. The check runs under a Robbie overlay over
 * the step column; a failed check shows in the same card — check again, and
 * after a second failure the buyer may go on without it. The StepBar sums up
 * the check and leads to Find suppliers (or, for the end user, the sign-off).
 */
interface WaitProps {
  state: ValidationStepState;
  components: number;
  long: boolean;
  onContinue: () => void;
}

export const ValidationOverlay: React.FC<WaitProps> = ({ state, components, long, onContinue }) => {
  const { isRunning, error, failures, hasRun, readOnly } = state;
  const failed = Boolean(error) && !isRunning;
  const secondary =
    failures >= 2 && !readOnly
      ? { label: validationCopy.skip, onClick: onContinue }
      : hasRun || readOnly
        ? { label: validationCopy.keep, onClick: state.dismissError }
        : undefined;
  return (
    <RobbieOverlay
      open={isRunning || failed}
      title={validationCopy.title(components)}
      sub={validationCopy.sub}
      usual={validationCopy.usual(components)}
      long={long}
      error={
        failed ? (
          <CompactError
            title={validationCopy.failedTitle}
            message={validationCopy.failedMessage}
            action={readOnly ? undefined : { label: validationCopy.retry, onClick: state.run }}
            secondaryAction={secondary}
          />
        ) : undefined
      }
    />
  );
};

interface BarProps {
  state: ValidationStepState;
  components: number;
  suggestions: number;
  onBack: () => void;
  onContinue: () => void;
  isContinueLoading: boolean;
  handoff?: EndUserHandoffView;
}

export const ValidationStepBar: React.FC<BarProps> = ({
  state,
  components,
  suggestions,
  onBack,
  onContinue,
  isContinueLoading,
  handoff,
}) => {
  const { isRunning, hasRun, failures } = state;
  const summary = hasRun && !isRunning ? (
    <>
      <strong className="font-semibold text-ink">{plural(components, 'component')}</strong> checked ·{' '}
      {plural(suggestions, 'suggestion')}
    </>
  ) : !isRunning && state.error === null && !hasRun ? (
    <>
      <strong className="font-semibold text-ink">{plural(components, 'component')}</strong> · not checked yet
    </>
  ) : (
    <>
      Checking <strong className="font-semibold text-ink">{plural(components, 'component')}</strong> ·
      suggestions appear here when I&apos;m done
    </>
  );
  // The end user's forward move is the sign-off, not Discovery — that (and
  // the research spend behind it) stays the buyer's call.
  const cta = handoff?.canFill
    ? {
        label: 'Sign off & hand back',
        onClick: () => handoff.signOff().catch(() => undefined),
        loading: handoff.isSigningOff,
        loadingLabel: 'Signing off…',
        icon: <CheckCircle2 />,
      }
    : {
        label: 'Find suppliers',
        onClick: onContinue,
        disabled: Boolean(handoff?.readOnly) || isRunning || (!hasRun && failures < 2),
        loading: isContinueLoading,
        loadingLabel: 'Starting research…',
      };
  return <StepBar stepLabel="Step 3 · Validation" summary={summary} onBack={onBack} cta={cta} />;
};
